import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const CookiesPolicyPage = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="cookies-policy-page">
    <Helmet>
      <title>Cookies Policy | Common Impact E-Learning</title>
      <meta name="description" content="Cookies Policy for Common Impact E-Learning. How we use cookies and how you can manage your preferences." />
    </Helmet>
    <Header />
    <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
      <h1 className="text-3xl sm:text-4xl font-bold text-[#2a3e5d] mb-2">Cookies Policy</h1>
      <p className="text-sm text-slate-500 mb-8">Last updated: 1 June 2026</p>

      <div className="prose prose-slate max-w-none prose-headings:text-[#2a3e5d] prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h3:text-lg prose-p:text-sm prose-p:leading-relaxed prose-li:text-sm">
        <h2>1. What Are Cookies?</h2>
        <p>Cookies are small text files placed on your device when you visit a website. They help the website remember your actions and preferences (such as login status, language, and display preferences) over a period of time, so you don't have to keep re-entering them whenever you return to the site.</p>

        <h2>2. How We Use Cookies</h2>
        <p>Common Impact E-Learning uses cookies and similar technologies to provide, protect, and improve our platform. We comply with the UK Privacy and Electronic Communications Regulations (PECR).</p>

        <h2>3. Types of Cookies We Use</h2>

        <h3>3.1 Essential Cookies</h3>
        <p>These cookies are strictly necessary for the platform to function. They cannot be switched off.</p>
        <table className="text-sm">
          <thead>
            <tr><th className="text-left p-2 bg-slate-50">Cookie</th><th className="text-left p-2 bg-slate-50">Purpose</th><th className="text-left p-2 bg-slate-50">Duration</th></tr>
          </thead>
          <tbody>
            <tr><td className="p-2 border-t">auth_token</td><td className="p-2 border-t">Authentication session (JWT)</td><td className="p-2 border-t">7 days</td></tr>
            <tr><td className="p-2 border-t">refresh_token</td><td className="p-2 border-t">Token refresh for seamless sessions</td><td className="p-2 border-t">30 days</td></tr>
            <tr><td className="p-2 border-t">cookie_consent</td><td className="p-2 border-t">Stores your cookie preference</td><td className="p-2 border-t">12 months</td></tr>
          </tbody>
        </table>

        <h3>3.2 Functional Cookies</h3>
        <p>These cookies enable enhanced functionality and personalisation.</p>
        <table className="text-sm">
          <thead>
            <tr><th className="text-left p-2 bg-slate-50">Cookie</th><th className="text-left p-2 bg-slate-50">Purpose</th><th className="text-left p-2 bg-slate-50">Duration</th></tr>
          </thead>
          <tbody>
            <tr><td className="p-2 border-t">text_size</td><td className="p-2 border-t">Remembers accessibility text size preference</td><td className="p-2 border-t">12 months</td></tr>
            <tr><td className="p-2 border-t">course_progress</td><td className="p-2 border-t">Locally caches course progress for offline resilience</td><td className="p-2 border-t">Session</td></tr>
          </tbody>
        </table>

        <h3>3.3 Analytics Cookies</h3>
        <p>These cookies help us understand how visitors use our platform. All data is anonymised.</p>
        <table className="text-sm">
          <thead>
            <tr><th className="text-left p-2 bg-slate-50">Cookie</th><th className="text-left p-2 bg-slate-50">Purpose</th><th className="text-left p-2 bg-slate-50">Duration</th></tr>
          </thead>
          <tbody>
            <tr><td className="p-2 border-t">_ga / _gid</td><td className="p-2 border-t">Google Analytics (if enabled) — page views, traffic sources</td><td className="p-2 border-t">2 years / 24 hours</td></tr>
          </tbody>
        </table>
        <p>Analytics cookies are only set if you consent to non-essential cookies.</p>

        <h3>3.4 Marketing Cookies</h3>
        <p>We do not currently use marketing or advertising cookies. If this changes, we will update this policy and request your consent.</p>

        <h2>4. Managing Your Cookie Preferences</h2>
        <p>When you first visit our platform, a cookie consent banner will appear allowing you to:</p>
        <ul>
          <li><strong>Accept All:</strong> Consent to all cookie categories</li>
          <li><strong>Reject Non-Essential:</strong> Only essential cookies will be used</li>
          <li><strong>Manage Preferences:</strong> Choose which non-essential cookie categories to enable</li>
        </ul>
        <p>You can change your preferences at any time by clearing your browser cookies and revisiting the site, or by using the cookie settings in your browser.</p>

        <h2>5. Browser Cookie Controls</h2>
        <p>Most browsers allow you to control cookies through their settings. Common options include:</p>
        <ul>
          <li><strong>Chrome:</strong> Settings &gt; Privacy and Security &gt; Cookies and other site data</li>
          <li><strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies and Site Data</li>
          <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Manage Website Data</li>
          <li><strong>Edge:</strong> Settings &gt; Cookies and Site Permissions</li>
        </ul>
        <p>Please note that disabling essential cookies may prevent the platform from functioning correctly.</p>

        <h2>6. Third-Party Cookies</h2>
        <p>Some third-party services we use may set their own cookies:</p>
        <ul>
          <li><strong>YouTube:</strong> If courses contain embedded YouTube videos, YouTube may set cookies for video playback functionality</li>
          <li><strong>Stripe:</strong> May set cookies during the payment process for fraud prevention</li>
        </ul>
        <p>These cookies are governed by the respective third party's privacy and cookie policies.</p>

        <h2>7. Changes to This Policy</h2>
        <p>We may update this Cookies Policy from time to time. The "Last updated" date at the top will reflect the most recent version.</p>

        <h2>8. Contact Us</h2>
        <p>If you have questions about our use of cookies, contact us at:<br /><a href="mailto:admin@commonimpactelearning.co.uk" className="text-[#20acf6]">admin@commonimpactelearning.co.uk</a></p>
      </div>
    </main>
    <Footer />
  </div>
);

export default CookiesPolicyPage;
